import React, { memo, useCallback, useContext, useEffect, useState } from "react"
import { onOpenSharingCatalogFunc } from "../../../types/knowledgeBaseFunctions"
import { shallowEqual } from "react-redux"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import { RootState } from "../../../store/rootReducer"
import {
    selectCatalog,
    selectCreateCategoryState,
    selectCurrentBranch,
    selectMoveCategoryState
} from "../../../store/knowledgeBase/selectors"
import AsyncState from "../../../core/asyncState"
import type { ItemId, TreeItem } from "@atlaskit/tree/types"
import type { TreeDestinationPosition, TreeSourcePosition } from "@atlaskit/tree"
import { isModifyPermitted } from "../../../utility/knowledgeBase/useModifyPermitted"
import { mutateCurrentBranch, toggleCategoryExpanded } from "../../../store/knowledgeBase/slice"
import { createCategory, getCategories, moveCategory } from "../../../store/knowledgeBase/thunks"
import { push } from "connected-react-router"
import { buildArticleActionUrl } from "../../../utility/knowledgeBase/articleUrlHelper"
import { ArticleAction, ArticleKind } from "../../../models/article"
import KnowledgeBasePermissionsProvider from "../../KnowledgeBasePermissionsProvider/KnowledgeBasePermissionsProvider"
import CatalogPopoverContainer from "../../CatalogPopover/CatalogPopoverContainer"
import CategoryAction from "../../CategoryAction/CategoryAction"
import { faPlus, faShareAlt } from "@fortawesome/pro-light-svg-icons"
import useRefCheck from "../../../hooks/useRefCheck"
import { useTranslation } from "react-i18next"
import { moveItem } from "../../../utility/common/tree"
import { KnowledgeBasePermittedAction } from "../../../models/knowledgeBase/permission"
import KnowledgeBaseContext from "../../KnowledgeBaseProvider/KnowledgeBaseContext"
import LazyCatalog from "../LazyCatalog"
import { SHARING_ENABLED } from "../../../utility/common/flags"
import { CatalogPlacement } from "../../Catalogs/Catalogs"
import { CategoryTreeData } from "../../../models/category"

const tNamespace = "knowledgeBase:"

export interface EditableCatalogProps {
    projectId: string
    id: string
    tree?: CategoryTreeData
    url: string
    canModifyKnowledgeBase: boolean
    catalogPlacement: CatalogPlacement
    onEditCatalogClick?: () => void
    onCancelEditCatalogClick?: () => void
    onOpenSharingCatalog?: onOpenSharingCatalogFunc
    onClickCategoryTitle?: (code: string) => void
    showContentOnHover?: boolean
    isDragEnabled?: boolean
    availableStatuses?: string[]
    excludeKind?: ArticleKind
    isHotKeysEnabled?: boolean
    onExpandCategoryCallback?: (categoryId: string) => void
}

const EditableCatalog = (props: EditableCatalogProps) => {
    const {
        projectId,
        id,
        url,
        tree: propsTree,
        catalogPlacement,
        onEditCatalogClick,
        onCancelEditCatalogClick,
        onOpenSharingCatalog,
        onClickCategoryTitle,
        showContentOnHover,
        isDragEnabled = true,
        availableStatuses = [],
        excludeKind,
        isHotKeysEnabled,
        onExpandCategoryCallback
    } = props
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { canModifyKnowledgeBase } = useContext(KnowledgeBaseContext)
    const storedTree = useAppSelector((state: RootState) => selectCatalog(state, id), shallowEqual)
    const tree = propsTree || storedTree
    const { CurrentCatalogCode } = useAppSelector(selectCurrentBranch, shallowEqual)
    const apiState = AsyncState.merge2(
        useAppSelector(selectMoveCategoryState),
        useAppSelector(selectCreateCategoryState)
    )
    const [draggableId, setDraggableId] = useState<ItemId | undefined>(undefined)
    const [editingId, setEditingId] = useState<string>()
    const isSharingEnabled = useRefCheck(SHARING_ENABLED)
    const enableExtensions = catalogPlacement === CatalogPlacement.knowledgeBase
    const rootItem = tree.items[tree.rootId] as TreeItem

    const handleDragStart = (draggableId: ItemId) => {
        setDraggableId(draggableId)
    }

    const handleDragEnd = (source: TreeSourcePosition, destination?: TreeDestinationPosition) => {
        const itemId = draggableId
        setDraggableId(undefined)

        if (apiState.inProcess) {
            return
        }

        if (!destination || !itemId) {
            return
        }

        if (source.index === destination.index && source.parentId === destination.parentId) {
            return
        }

        const {
            data: { permittedAction: parentPermittedAction, catalogCode: parentCatalogCode }
        } = tree.items[destination.parentId]

        if (!isModifyPermitted(parentPermittedAction)) {
            return
        }

        const { newTree, draggableItem, sourceItem, destinationItem, prevItem, nextItem } = moveItem(
            itemId,
            tree,
            source,
            destination
        )

        if (CurrentCatalogCode !== parentCatalogCode) {
            dispatch(
                mutateCurrentBranch({
                    CurrentCatalogCode: parentCatalogCode
                })
            )
        }

        dispatch(moveCategory(projectId, newTree, sourceItem, destinationItem, draggableItem, prevItem, nextItem))
    }

    const handleCategoryTitleClick = useCallback(
        (categoryItem: TreeItem, catalogCode?: string, categoryCode?: string) => {
            if (categoryCode) {
                dispatch(
                    mutateCurrentBranch({
                        CurrentCategoryId: categoryItem.id as string,
                        CurrentCatalogCode: catalogCode
                    })
                )
                if (catalogPlacement !== CatalogPlacement.knowledgeBase) {
                    onClickCategoryTitle && onClickCategoryTitle(categoryCode)
                } else {
                    const newUrl = buildArticleActionUrl(url, ArticleAction.View, categoryCode)
                    dispatch(push(newUrl))
                }
            }
        },
        [dispatch, url, catalogPlacement, onClickCategoryTitle]
    )

    const handleAddCategory = useCallback(
        (item: TreeItem) => {
            const children = item.children
            const firstChildrenId = children.length ? children[0] : undefined
            const firstChildren = firstChildrenId ? tree.items[firstChildrenId] : undefined
            dispatch(createCategory(projectId, item, url, firstChildren))
        },
        [projectId, dispatch, url, tree.items]
    )

    useEffect(() => {
        if (editingId === undefined) {
            return
        }

        if (!editingId && onCancelEditCatalogClick) {
            onCancelEditCatalogClick()
        } else {
            onEditCatalogClick && onEditCatalogClick()
        }
    }, [editingId, onCancelEditCatalogClick, onEditCatalogClick])

    const handleDragEnabled = (item: TreeItem) => {
        const {
            data: { permittedAction }
        } = item

        return !apiState.inProcess && isModifyPermitted(permittedAction)
    }

    const handleSharingClick = () => {
        onOpenSharingCatalog?.(id)
    }

    const handleToggle = (itemId: ItemId, isExpanded: boolean) => {
        dispatch(
            toggleCategoryExpanded({
                CatalogId: tree.rootId,
                CategoryId: itemId,
                IsExpanded: isExpanded
            })
        )
    }

    const handleExpandCategory = useCallback(
        (itemId: ItemId, onExpand: (itemId: ItemId) => void) => {
            const { children, isChildrenLoading } = tree.items[itemId]

            if (!apiState.inProcess) {
                if (onExpandCategoryCallback) {
                    onExpandCategoryCallback(itemId as string)
                }

                onExpand(itemId)

                if (!isChildrenLoading && children.length === 0) {
                    dispatch(getCategories(projectId, itemId, tree.rootId))
                }
            }
        },
        [tree.items, tree.rootId, apiState.inProcess, dispatch, projectId]
    )

    const handleCanEdit = (permittedAction?: KnowledgeBasePermittedAction) => {
        return canModifyKnowledgeBase && isModifyPermitted(permittedAction)
    }

    const getAddCategoryAction = (item: TreeItem) => (
        <CategoryAction
            id={id}
            key={`${id}-add-article`}
            tooltipText={t(`${tNamespace}add-article`)}
            icon={faPlus}
            onClick={() => handleAddCategory(item)}
            loading={item.isChildrenLoading || apiState.inProcess}
        />
    )

    const handleGetItemExtensions = (item: TreeItem) => [getAddCategoryAction(item)]

    const getExtensions = () => {
        const result = [
            <KnowledgeBasePermissionsProvider
                key={`${id}-settings`}
                permissions={rootItem.data.permissions}
                withNested={rootItem.data.permissionsWithNested ?? false}
            >
                <CatalogPopoverContainer
                    catalogCode={rootItem.data.catalogCode}
                    catalogTitle={rootItem.data.title}
                    onEditClick={id => setEditingId(prevId => (prevId === id ? "" : id))}
                    onCancelEditClick={() => setEditingId("")}
                    isEdit
                    isNewsline={rootItem.data.newsline}
                />
            </KnowledgeBasePermissionsProvider>,
            getAddCategoryAction(rootItem)
        ]
        if (onOpenSharingCatalog && isSharingEnabled) {
            result.unshift(
                <CategoryAction
                    key={`${id}-share-category`}
                    id="share-category"
                    icon={faShareAlt}
                    tooltipText={t(`${tNamespace}article-actions.share`)}
                    onClick={handleSharingClick}
                />
            )
        }
        return result
    }

    return (
        <LazyCatalog
            tree={tree}
            editingId={editingId}
            extensions={enableExtensions ? getExtensions() : undefined}
            onGetItemExtensions={enableExtensions ? handleGetItemExtensions : undefined}
            onTitleClick={handleCategoryTitleClick}
            onCanEdit={handleCanEdit}
            onExpandCategory={handleExpandCategory}
            onToggle={handleToggle}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            isDragEnabled={isDragEnabled && handleDragEnabled}
            showContentOnHover={showContentOnHover}
            availableStatuses={availableStatuses}
            excludeKind={excludeKind}
            asLink={catalogPlacement === CatalogPlacement.knowledgeBase}
            isHotKeysEnabled={isHotKeysEnabled}
        />
    )
}

export default memo(EditableCatalog)
